import { Suspense, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal } from '@givebutter/util';
import { faCircleQuestion, faDollarCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCampaignPayouts } from 'api/payouts';
import * as WithdrawFundsDialog from 'assets/js/pages/payouts/WithdrawFundsDialog';
import { useFetchPayoutAccount } from 'assets/js/pages/payouts/useFetchPayoutAccount';
import { WithdrawFundsProvider } from 'assets/js/pages/payouts/useWithdrawFunds';
import { useReactTableState } from 'components/table';
import * as Table from 'components/table';
import * as Tooltip from 'components/tooltip';
import { posthogEvents } from 'constants/posthog-events';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { usePosthog } from 'customHooks/usePosthog';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twJoin } from 'tailwind-merge';
import { Campaign, PayoutFilter } from 'types/domain';

interface PayoutsTableProps {
    filter: PayoutFilter;
}

const columnHelper = createColumnHelper<Campaign>();

const PayoutsTable = (props: PayoutsTableProps) => {
    const { filter } = props;
    const { pushEvent } = usePosthog();
    const { accountId, defaultPayoutTip } = useCurrentAccount();
    const currencySymbol = useCurrencySymbol();
    const { pagination, setPagination, expanded, setExpanded } = useReactTableState();
    const [isPending, params] = useIsPending({ page: pagination.pageIndex + 1, filter }, 500);

    const { data: payoutAccount } = useFetchPayoutAccount();

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.campaignPayoutsWithFilters(accountId, params),
        queryFn: () => fetchCampaignPayouts(accountId, params),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const hasBankAccount = payoutAccount.has_bank_account;

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: Table.Constants.Expand,
                cell: info => <Table.RowExpand onClick={e => e.stopPropagation()} row={info.row} />,
            }),
            columnHelper.display({
                id: 'source',
                cell: info => {
                    const campaign = info.row.original;

                    return (
                        <div className={'flex flex-col'}>
                            <p className="font-medium">{campaign.creator_name}</p>
                            <p>
                                <a
                                    target="_blank"
                                    href={campaign.url}
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                >
                                    {campaign.title}
                                </a>
                            </p>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('balance', {
                cell: info => {
                    const balance = info.getValue();

                    return (
                        <div className={'flex flex-col py-2'}>
                            <div className={'flex items-center gap-2'}>
                                <p className={'text-medium w-20 text-right text-sm text-gray-600'}>
                                    Total raised
                                </p>
                                <p className={'text-sm font-medium'}>
                                    ${maskDecimal(balance?.amount ?? 0)}
                                </p>
                            </div>
                            <div className={'flex items-center gap-2'}>
                                <p className={'text-medium w-20 text-right text-sm text-gray-600'}>
                                    Pending
                                </p>
                                <Tooltip.Root>
                                    <Tooltip.Trigger className={'text-sm font-medium'}>
                                        ${maskDecimal(balance?.pending ?? 0)}
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse>
                                            <p>
                                                Donations remain "pending" for 48 to 72 hours after
                                                they're made to ensure the funds settle properly.
                                                ACH donations will remain "pending" for up to 7
                                                business days to ensure the funds settle properly.
                                            </p>
                                            <Tooltip.Arrow inverse />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            </div>
                            <div className={'flex items-center gap-2'}>
                                <p className={'text-medium w-20 text-right text-sm text-gray-600'}>
                                    Available
                                </p>
                                <Tooltip.Root>
                                    <Tooltip.Trigger>
                                        <Badge
                                            size={'sm'}
                                            className={twJoin(
                                                '-ml-1 text-sm',
                                                ...getAvailableBalanceClassNames(
                                                    balance?.available ?? 0,
                                                ),
                                            )}
                                        >
                                            ${maskDecimal(balance?.available ?? 0)}
                                        </Badge>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse>
                                            <p>
                                                Bank transfers typically take 1-2 business days to
                                                appear in your bank account after a payout is
                                                requested.
                                            </p>
                                            <Tooltip.Arrow inverse />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            </div>
                        </div>
                    );
                },
                meta: {
                    className: '[&>*]:w-[250px]',
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const campaign = info.row.original;

                    return (
                        <div className={'flex items-center'}>
                            {hasBankAccount && (
                                <Suspense fallback={<Loader />}>
                                    <WithdrawFundsProvider
                                        key={`${campaign.id}-${campaign.balance?.available}-${defaultPayoutTip}`}
                                        campaign={campaign}
                                    >
                                        <WithdrawFundsDialog.Root>
                                            <WithdrawFundsDialog.Trigger asChild={true}>
                                                <Button
                                                    onClick={() =>
                                                        pushEvent(posthogEvents.PAYOUT_PAID_STARTED)
                                                    }
                                                    variant="outline"
                                                    color="neutral"
                                                >
                                                    Withdraw
                                                </Button>
                                            </WithdrawFundsDialog.Trigger>
                                        </WithdrawFundsDialog.Root>
                                    </WithdrawFundsProvider>
                                </Suspense>
                            )}
                            {!hasBankAccount && (
                                <>
                                    <Button disabled={true} variant="outline" color="neutral">
                                        Withdraw
                                    </Button>
                                    <Tooltip.Root>
                                        <Tooltip.Trigger>
                                            <FontAwesomeIcon
                                                icon={faCircleQuestion}
                                                className="ml-3 text-blue-500"
                                            />
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content inverse>
                                                <p>
                                                    Please add a bank account above before
                                                    continuing.
                                                </p>
                                                <Tooltip.Arrow inverse />
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                </>
                            )}
                        </div>
                    );
                },
                meta: {
                    className: 'p-0 [&>*]:float-right',
                    ignoreClick: true,
                },
            }),
        ],
        [hasBankAccount, defaultPayoutTip, pushEvent],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta?.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            expanded,
        },
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        enableSorting: false,
        manualPagination: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            rowClassName: 'h-auto [&>*]:h-auto',
            onRowClick: row => row.toggleExpanded(),
            renderExpandedRow: row => {
                const campaign = row.original;

                return (
                    <tr className={'bg-gray-50'}>
                        <td />
                        <td colSpan={row.getVisibleCells().length}>
                            <div className="flex flex-col gap-4 p-4">
                                <div className={'flex justify-between gap-4'}>
                                    <div className={'flex flex-col'}>
                                        <p className={'font-medium'}>Total raised online</p>
                                        <p className={'text-sm text-gray-600'}>
                                            Total amount raised from online transactions less any
                                            refunds, disputes, and offline transactions.
                                        </p>
                                    </div>
                                    <p className={'font-medium'}>
                                        {currencySymbol}
                                        {maskDecimal(campaign.balance?.amount ?? 0)}
                                    </p>
                                </div>
                                <div className={'flex justify-between gap-4'}>
                                    <div className={'flex flex-col'}>
                                        <p className={'font-medium'}>
                                            Fees not covered by supporters
                                        </p>
                                        <p className={'text-sm text-gray-600'}>
                                            The amount of processing & platform fees not covered by
                                            your supporters.
                                        </p>
                                        <p className={'text-sm font-medium text-green-600'}>
                                            {campaign.balance?.fees_covered_percent ?? 0}% of your
                                            supporters covered the fees.
                                        </p>
                                    </div>
                                    <p className={'font-medium'}>
                                        {campaign.balance?.fees_not_covered &&
                                        campaign.balance?.fees_not_covered > 0
                                            ? '-'
                                            : ''}
                                        {currencySymbol}
                                        {maskDecimal(campaign.balance?.fees_not_covered ?? 0)}
                                    </p>
                                </div>
                                <Separator />
                                <div className={'flex justify-between gap-4'}>
                                    <div className={'flex flex-col'}>
                                        <p className={'font-medium'}>Pending balance</p>
                                        <p className={'text-sm text-gray-600'}>
                                            Donations remain "pending" for 48 to 72 hours after
                                            they're made to ensure the funds settle properly. ACH
                                            donations will remain "pending" for up to 7 business
                                            days to ensure the funds settle properly.
                                        </p>
                                    </div>
                                    <p className={'font-medium'}>
                                        {currencySymbol}
                                        {maskDecimal(campaign.balance?.pending ?? 0)}
                                    </p>
                                </div>
                                <div className={'flex justify-between gap-4'}>
                                    <div className={'flex flex-col'}>
                                        <p className={'font-medium'}>Paid out</p>
                                        <p className={'text-sm text-gray-600'}>
                                            Funds you’ve already withdrawn. Visit the Payout History
                                            tab above for more details.
                                        </p>
                                    </div>
                                    <p className={'font-medium'}>
                                        {currencySymbol}
                                        {maskDecimal(campaign.balance?.paid_out ?? 0)}
                                    </p>
                                </div>
                                <Separator />
                                <div className={'flex justify-between gap-4'}>
                                    <div className={'flex flex-col'}>
                                        <p className={'font-medium'}>Available balance</p>
                                        <p className={'text-sm text-gray-600'}>
                                            Funds available to be withdrawn to your connected
                                            account.
                                        </p>
                                    </div>
                                    <div>
                                        <Badge
                                            size={'sm'}
                                            className={twJoin(
                                                'text-base',
                                                ...getAvailableBalanceClassNames(
                                                    campaign.balance?.available ?? 0,
                                                ),
                                            )}
                                        >
                                            {currencySymbol}
                                            {maskDecimal(campaign.balance?.available ?? 0)}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                );
            },
        },
    });

    return (
        <div className={'flex flex-col gap-4'}>
            <Table.Root isLoading={isPending} variant={'bordered'} table={table}>
                <Box className={'overflow-x-auto'}>
                    <Table.Content>
                        <Table.Body />
                    </Table.Content>
                    <Table.Empty variant={'primary'}>
                        <Table.EmptyIcon>
                            <FontAwesomeIcon icon={faDollarCircle} />
                        </Table.EmptyIcon>
                        <Table.EmptyTitle>No payouts found</Table.EmptyTitle>
                        <Table.EmptyDescription>
                            Withdrawable funds will be displayed here when they become available.
                        </Table.EmptyDescription>
                    </Table.Empty>
                </Box>
                <Table.Pagination>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </Table.Root>
        </div>
    );
};

const getAvailableBalanceClassNames = (available: number) => [
    available > 0 && 'bg-green-600 text-white',
    available < 0 && 'bg-red-600 text-white',
];

export { PayoutsTable };
